import { Source } from './Source.interface';

export type SourceTypes = {
  [key in SourceType]: SourceTypeSchema;
};

export type DestinationTypes = {
  [key in DestinationType]: DestinationTypeSchema;
};

export type SourceType =
  | 'AIPRISE'
  | 'DOCUSIGN'
  | 'PUBLISH_API'
  | 'HTTP'
  | 'WEBHOOK'
  | 'MANAGED'
  | 'BRIDGE'
  | 'EBAY'
  | 'FIREBLOCKS'
  | 'FRONTAPP'
  | 'TWITTER'
  | 'TWILIO'
  | 'STRIPE'
  | 'RECHARGE'
  | 'GITHUB'
  | 'SHOPIFY'
  | 'POSTMARK'
  | 'TALLY'
  | 'TYPEFORM'
  | 'XERO'
  | 'SVIX'
  | 'REPLICATE'
  | 'ADYEN'
  | 'AKENEO'
  | 'ZOOM'
  | 'OURA'
  | 'GITLAB'
  | 'PROPERTY-FINDER'
  | 'WOOCOMMERCE'
  | 'COMMERCELAYER'
  | 'HUBSPOT'
  | 'MAILGUN'
  | 'MERAKI'
  | 'PICQER'
  | 'PERSONA'
  | 'PIPEDRIVE'
  | 'SENDGRID'
  | 'WORKOS'
  | 'SYNCTERA'
  | 'AWS_SNS'
  | 'THREE_D_EYE'
  | 'ENODE'
  | 'FAVRO'
  | 'LINEAR'
  | 'TWITCH'
  | 'WIX'
  | 'NMI'
  | 'ORB'
  | 'PYLON'
  | 'TRELLO'
  | 'REPAY'
  | 'SANITY'
  | 'SHOPLINE'
  | 'SQUARE'
  | 'SOLIDGATE'
  | 'CLOUDSIGNAL'
  | 'COURIER'
  | 'TELNYX'
  | 'DISCORD'
  | 'TOKENIO'
  | 'FISERV'
  | 'BONDSMITH'
  | 'VERCEL_LOG_DRAINS'
  | 'VERCEL'
  | 'TEBEX'
  | 'SLACK'
  | 'RAZORPAY'
  | 'MAILCHIMP'
  | 'NUVEMSHOP'
  | 'PADDLE'
  | 'PAYPAL'
  | 'TREEZOR'
  | 'PRAXIS'
  | 'CUSTOMERIO'
  | 'FACEBOOK'
  | 'WHATSAPP'
  | 'TIKTOK'
  | 'AIRWALLEX'
  | 'ZENDESK'
  | 'UPOLLO'
  | 'BIGCOMMERCE'
  | 'POLAR'
  | 'SMILE'
  | 'GOCARDLESS'
  | 'LINKEDIN'
  | 'STRAVA'
  | 'QUOTER'
  | 'OKTA'
  | 'RING_CENTRAL';

export type SourceGenericAuthTypes = 'HMAC' | 'BASIC_AUTH' | 'API_KEY' | 'STANDARD_WEBHOOKS';
export type SourceAuthProvider = SourceGenericAuthTypes | Exclude<SourceType, 'HTTP' | 'WEBHOOK'>;

export type TypeFeatureField = {
  label: string;
  name: string;
  type: 'text' | 'select' | 'password' | 'number' | 'textarea' | 'checkbox' | 'list' | 'object';
  customSchema?: (schema: any) => any;
  placeholder?: string;
  required?: boolean;
  allow_empty?: boolean;
  features?: IntegrationFeature[];
  options?: { value: string; label: string }[];
};
export type TypeFeatureFields = TypeFeatureField[];

export type SourceTypeFeatureTypes =
  | IntegrationFeature
  | 'CUSTOM_HTTP_METHOD'
  | 'CUSTOM_RESPONSE'
  | 'MANAGED_TOKEN'
  | 'PUBLISH_API';

type SourceTypeManagedCustomResponse = {
  managed: true;
  default_value: ('GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE')[];
};

type SourceTypeFeatureTypesManaged = SourceTypeManagedCustomResponse;

export type SourceTypeFeatureTypesUnmanaged = {
  managed?: false;
  required?: boolean;
  advanced?: boolean;
  default_value?: string;
  fields?: TypeFeatureFields;
  auth?: {
    [key in SourceGenericAuthTypes]: {
      label: string;
      features: IntegrationFeature[];
      fields: TypeFeatureFields;
    };
  };
};
export type SourceTypeFeature = SourceTypeFeatureTypesManaged | SourceTypeFeatureTypesUnmanaged;
export function isSourceTypeFeatureUnmanaged(
  feature: { managed?: boolean } | undefined
): feature is SourceTypeFeatureTypesUnmanaged {
  return feature?.managed !== true;
}
type FeatureOptions = SourceTypeFeatureTypesManaged | SourceTypeFeatureTypesUnmanaged;

export type SourceTypeFeatures = Partial<{
  [K in SourceTypeFeatureTypes]: FeatureOptions;
}>;

export interface SourceTypeSchema {
  label: string;
  category: 'platform' | 'http';
  description?: string;
  hidden?: boolean;
  popular?: boolean;
  icon?: string;
  features: SourceTypeFeatures;
  configuration_alert?: string;
  configuration_link?: string;
  configuration_link_text?: string;
  external_docs_url?: string;
  icon_domain?: string;
}

export type DestinationType = 'HTTP' | 'CLI' | 'MOCK_API';

export type DestinationTypeFeatureTypes =
  | 'CUSTOM_URL'
  | 'VERIFICATION'
  | 'CUSTOM_DESTINATION_HTTP_METHOD'
  | 'CUSTOM_CLI_PATH'
  | 'MAX_DELIVERY_RATE'
  | 'PATH_FORWARDING';

type GENERIC_AUTH_TYPES =
  | 'HOOKDECK_SIGNATURE'
  | 'CUSTOM_SIGNATURE'
  | 'BASIC_AUTH'
  | 'API_KEY'
  | 'BEARER_TOKEN'
  | 'OAUTH2_CLIENT_CREDENTIALS'
  | 'OAUTH2_AUTHORIZATION_CODE'
  | 'AWS_SIGNATURE';

export type DestinationTypeFeatureTypesManaged = {
  managed: true;
  default_value: string;
};

export type DestinationTypeFeatureTypesUnmanaged = {
  managed?: false;
  required?: boolean;
  advanced?: boolean;
  default_value?: string;
  fields?: TypeFeatureFields;
  auth?: {
    [key in GENERIC_AUTH_TYPES]: {
      label: string;
      fields: TypeFeatureFields;
    };
  };
};
export type DestinationTypeFeature =
  | DestinationTypeFeatureTypesManaged
  | DestinationTypeFeatureTypesUnmanaged;

export const isDestinationTypeFeatureUnmanaged = (
  feature: { managed?: boolean } | undefined
): feature is DestinationTypeFeatureTypesUnmanaged => feature?.managed !== true;

export type DestinationTypeFeatures = Partial<{
  [key in DestinationTypeFeatureTypes]: DestinationTypeFeature;
}>;
export interface DestinationTypeSchema {
  label: string;
  category: 'aws' | 'generic';
  description?: string;
  icon?: string;
  features: DestinationTypeFeatures;
  configuration_alert?: string;
}

export const basic_auth_integration_providers = ['BASIC_AUTH', 'POSTMARK', 'PIPEDRIVE'];

export interface HMACIntegrationConfigs {
  webhook_secret_key: string;
  algorithm: HMACAlgorithms;
  header_key: string;
  encoding: 'base64' | 'base64url' | 'hex';
  delimiter: '.' | ':';
  prefix_data_with_timestamp: boolean;
}

export interface HandledHMACConfigs extends Pick<HMACIntegrationConfigs, 'webhook_secret_key'> {}

export interface BasicAuthIntegrationConfigs {
  username: string;
  password: string;
}

export interface APIKeyIntegrationConfigs {
  header_key: string;
  api_key: string;
}

export interface Crc32IntegrationConfigs {
  webhook_id: string;
}

export interface EbayIntegrationConfigs {
  client_id: string;
  client_secret: string;
  dev_id: string;
  verification_token: string;
  environment: string;
}

export interface FiservIntegrationConfigs {
  webhook_secret_key: string;
  store_name: string;
}

export interface HandledAPIKeyIntegrationConfigs extends Pick<APIKeyIntegrationConfigs, 'api_key'> {
  api_key: string;
}

export interface PublicKeyIntegrationConfigs {
  public_key: string;
}

export interface ShopifyIntegrationConfigs
  extends Pick<HMACIntegrationConfigs, 'webhook_secret_key'> {
  rate_limit_period?: 'minute' | 'second';
  rate_limit?: number;
  api_key?: string;
  api_secret?: string;
  shop?: string;
}

export interface VercelLogDrainsIntegrationConfigs {
  webhook_secret_key?: string;
  vercel_log_drains_secret?: string;
}

export interface SvixIntegrationConfigs extends HandledHMACConfigs {
  headers_prefix: string;
}

export interface StandardWebhooksIntegrationConfigs {
  webhook_secret_key: string;
  headers_prefix: string;
}

export interface TiktokIntegrationConfigs {
  webhook_secret_key: string;
  app_key: string;
}

export interface ManagedTokenIntegrationConfigs {
  token: string;
}

export type HMACAlgorithms = 'md5' | 'sha1' | 'sha256' | 'sha512';

export type IntegrationConfig =
  | APIKeyIntegrationConfigs
  | EbayIntegrationConfigs
  | HandledAPIKeyIntegrationConfigs
  | HandledHMACConfigs
  | SvixIntegrationConfigs
  | HMACIntegrationConfigs
  | StandardWebhooksIntegrationConfigs
  | APIKeyIntegrationConfigs
  | BasicAuthIntegrationConfigs
  | ShopifyIntegrationConfigs
  | PublicKeyIntegrationConfigs
  | VercelLogDrainsIntegrationConfigs
  | Crc32IntegrationConfigs
  | ManagedTokenIntegrationConfigs;

export type IntegrationFeature = 'VERIFICATION' | 'HANDSHAKE';

export interface Integration {
  id: string;
  team_id: string;
  label: string;
  provider: SourceAuthProvider;
  features: IntegrationFeature[];
  configs: IntegrationConfig;
  sources: Source[];
  updated_at: string;
  created_at: string;
}

export interface IntegrationCreateInput {
  label: string;
  team_id: string;
  configs: object;
  provider: SourceAuthProvider;
  features: IntegrationFeature[];
  encrypted_configs?: string;
  iv?: string;
}

export interface IntegrationUpdateInput {
  label?: string;
  team_id?: string;
  configs: object;
  features?: IntegrationFeature[];
  encrypted_configs?: string;
  iv?: string;
}
